import React, { Children, cloneElement, isValidElement, useEffect, useState } from 'react';
import classNames from 'classnames';
import { StepperContext } from './StepperContext';
import type { StepperNumberProps } from './StepperNumber';
import { breakPointMd } from '../../utils/constants';
import styles from './Stepper.module.scss';
import { StepperMobile } from './StepperMobile';

export type StepperProps = {
  activeStep: number;
  className?: string;
  children: React.ReactNode;
};

export function Stepper({ className, children, activeStep }: StepperProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakPointMd);
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const childrenArray = Children.toArray(children);
  const modifiedChildren = Children.map(childrenArray, (child, index) => {
    if (isValidElement<StepperNumberProps>(child)) {
      let state: 'active' | 'done' | 'disabled';
      if (index + 1 === activeStep) {
        state = 'active';
      } else if (index + 1 < activeStep) {
        state = 'done';
      } else {
        state = 'disabled';
      }

      return cloneElement(child, {
        ...child.props,
        state,
        number: index + 1,
      });
    }

    return child;
  });

  const clampedIndex = Math.min(activeStep - 1, (modifiedChildren?.length ?? 1) - 1);
  const activeChild = modifiedChildren?.[clampedIndex];
  const activeChildProps = isValidElement<StepperNumberProps>(activeChild)
    ? activeChild.props
    : ({} as StepperNumberProps);

  return (
    <StepperContext.Provider value={{ steps: childrenArray.length }}>
      {isMobile ? (
        <StepperMobile
          activeStep={activeStep}
          className={className}
          description={activeChildProps.description ?? ''}
          label={activeChildProps.label ?? ''}
          steps={childrenArray.length}
        />
      ) : (
        <div className={classNames(styles.stepper, className)}>{modifiedChildren}</div>
      )}
    </StepperContext.Provider>
  );
}

export default Stepper;
